import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mic, Loader2, Ticket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../utils/api';
import { APP_FEATURES } from '../types';

const Register = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [inviteRequired, setInviteRequired] = useState(APP_FEATURES.inviteRequired);
  const navigate = useNavigate();

  // 动态获取邀请码设置
  useEffect(() => {
    authApi.getPublicSettings()
      .then(res => setInviteRequired(res.data.invite_required ?? false))
      .catch(() => { /* 使用默认值 */ });
  }, []);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError(tCommon('errors.passwordMismatch'));
      return;
    }

    if (inviteRequired && !invitationCode.trim()) {
      setError(tCommon('errors.requireInvitationCode'));
      return;
    }

    setLoading(true);
    try {
      if (APP_FEATURES.emailEnabled) {
        // 邮箱模式：需要验证码
        const response = await authApi.register(username, email, password, invitationCode.trim() || undefined);
        setSuccess(response.data.message);
        setStep('verify');
      } else {
        // 简化模式：直接注册成功
        await authApi.register(username, email, password, invitationCode.trim() || undefined);
        setSuccess(t('register.success'));
        setTimeout(() => navigate('/login'), 1500);
      }
    } catch (err: any) {
      let errorMsg = t('register.error');
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('; ');
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authApi.verifyEmail(email, code);
      setSuccess(response.data.message);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      let errorMsg = t('register.verify.error');
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('; ');
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await authApi.register(username, email, password);
      setSuccess(response.data.message);
    } catch (err: any) {
      let errorMsg = t('register.resendError');
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('; ');
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="w-full max-w-md">
        <div className="card">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
              <Mic className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{t('register.title')}</h1>
            <p className="text-gray-500 mt-1">{t('register.subtitle')}</p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm mb-4">
              {success}
            </div>
          )}

          {step === 'form' ? (
            <>
              {/* 注册表单 */}
              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('register.usernameLabel')}
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="input"
                    placeholder={t('register.usernamePlaceholder')}
                    required
                    minLength={3}
                    maxLength={50}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('register.emailLabel')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder={t('register.emailPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('register.passwordLabel')}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input"
                    placeholder={t('register.passwordPlaceholder')}
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('register.confirmPasswordLabel')}
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input"
                    placeholder={t('register.confirmPasswordPlaceholder')}
                    required
                    minLength={6}
                  />
                </div>

                {inviteRequired && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="inline-flex items-center gap-1">
                        <Ticket className="w-4 h-4" />
                        {t('register.invitationCodeLabel')} <span className="text-red-500">*</span>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={invitationCode}
                      onChange={(e) => setInvitationCode(e.target.value)}
                      className="input"
                      placeholder={t('register.invitationCodePlaceholder')}
                      required
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('register.loading')}
                    </>
                  ) : (
                    t('register.submit')
                  )}
                </button>
              </form>

              {/* 返回登录 */}
              <div className="mt-6 text-center text-sm text-gray-500">
                <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                  {t('register.hasAccount')}
                </Link>
              </div>
            </>
          ) : (
            <>
              {/* 验证码输入 */}
              <form onSubmit={handleVerify} className="space-y-5">
                <div className="text-center mb-2">
                  <p className="text-gray-600">
                    {t('register.verify.codeSentTo')} <span className="font-medium text-gray-900">{email}</span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('register.verify.codeLabel')}
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input text-center text-2xl tracking-[0.5em] font-mono"
                    placeholder={t('register.verify.codePlaceholder')}
                    required
                    maxLength={6}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('register.verify.loading')}
                    </>
                  ) : (
                    t('register.verify.submit')
                  )}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {t('register.verify.resend')}
                  </button>
                </div>
              </form>

              <div className="mt-6 text-center text-sm text-gray-500">
                <button
                  onClick={() => setStep('form')}
                  className="text-gray-500 hover:text-gray-700 font-medium"
                >
                  {t('register.verify.backToForm')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
